import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import MarketingPublicNav from '../components/MarketingPublicNav';
import { PublicBandSection, PublicMarketingLayout } from '../components/PublicMarketingLayout';

const Help: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('getting-started');

  const categories = [
    { id: 'getting-started', name: 'Getting Started', icon: '🚀', count: 8 },
    { id: 'brands', name: 'For Brands', icon: '🏢', count: 12 },
    { id: 'kols', name: 'For KOLs', icon: '⭐', count: 15 },
    { id: 'campaigns', name: 'Campaigns', icon: '📢', count: 10 },
    { id: 'payments', name: 'Payments & Billing', icon: '💳', count: 7 },
    { id: 'technical', name: 'Technical Issues', icon: '🔧', count: 6 }
  ];

  const helpArticles = [
    {
      id: 'gs-1',
      category: 'getting-started',
      title: 'How to create your kol.my account',
      excerpt: 'Step-by-step guide to signing up and setting up your profile',
      views: 1250
    },
    {
      id: 'gs-2',
      category: 'getting-started',
      title: 'Understanding the dashboard',
      excerpt: 'Overview of all dashboard features and navigation',
      views: 980
    },
    {
      id: 'brand-1',
      category: 'brands',
      title: 'How to post a campaign brief',
      excerpt: 'Complete guide to creating effective campaign briefs',
      views: 1500
    },
    {
      id: 'brand-2',
      category: 'brands',
      title: 'Finding the right KOLs',
      excerpt: 'Using filters and AI matching to discover influencers',
      views: 1320
    },
    {
      id: 'kol-1',
      category: 'kols',
      title: 'Optimizing your KOL profile',
      excerpt: 'Tips to increase your visibility and campaign offers',
      views: 2100
    },
    {
      id: 'kol-2',
      category: 'kols',
      title: 'Managing campaign invitations',
      excerpt: 'How to accept, negotiate, or decline offers',
      views: 1850
    },
    {
      id: 'campaign-1',
      category: 'campaigns',
      title: 'Tracking campaign progress',
      excerpt: 'Monitoring milestones and deliverables',
      views: 1100
    },
    {
      id: 'payment-1',
      category: 'payments',
      title: 'Understanding payment milestones',
      excerpt: 'How milestone-based payments work',
      views: 1450
    }
  ];

  const filteredArticles = helpArticles.filter(article => article.category === selectedCategory);

  return (
    <PublicMarketingLayout>
      <MarketingPublicNav />

      <PublicBandSection band={1} className="px-6 py-20">
        <div className="mx-auto max-w-6xl text-center">
          <h1 className="mb-6 bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-5xl font-bold text-transparent md:text-6xl">
            Help Center
          </h1>
          <p className="mx-auto mb-8 max-w-4xl text-xl text-gray-400 md:text-2xl">
            Find answers to your questions and learn how to make the most of kol.my
          </p>

          <div className="mx-auto max-w-2xl">
            <div className="relative">
              <input
                type="text"
                placeholder="Search for help articles..."
                className="w-full rounded-lg border border-white/20 bg-white/10 px-6 py-4 text-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <button
                type="button"
                className="absolute right-4 top-1/2 -translate-y-1/2 transform rounded-lg bg-white px-6 py-2 font-semibold text-violet-600 transition hover:bg-gray-100"
              >
                Search
              </button>
            </div>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={2} className="px-6 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-3xl font-bold mb-8 text-center bg-gradient-to-r from-violet-500 to-blue-500 bg-clip-text text-transparent">Popular Topics</h2>
          <div className="grid md:grid-cols-4 gap-6">
            <Link to="/faq" className="p-6 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition text-center">
              <div className="text-4xl mb-3">❓</div>
              <h3 className="font-bold text-white mb-2">FAQ</h3>
              <p className="text-sm text-gray-400">Frequently asked questions</p>
            </Link>
            <Link to="/support" className="p-6 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition text-center">
              <div className="text-4xl mb-3">💬</div>
              <h3 className="font-bold text-white mb-2">Contact Support</h3>
              <p className="text-sm text-gray-400">Get help from our team</p>
            </Link>
            <Link to="/blog" className="p-6 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition text-center">
              <div className="text-4xl mb-3">📚</div>
              <h3 className="font-bold text-white mb-2">Blog</h3>
              <p className="text-sm text-gray-400">Tips and guides</p>
            </Link>
            <Link to="/tutorials" className="p-6 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition text-center">
              <div className="text-4xl mb-3">🎥</div>
              <h3 className="font-bold text-white mb-2">Video Tutorials</h3>
              <p className="text-sm text-gray-400">Step-by-step videos</p>
            </Link>
            <Link to="/guides" className="p-6 bg-white/5 border border-white/10 rounded-xl hover:bg-white/10 transition text-center">
              <div className="text-4xl mb-3">🧭</div>
              <h3 className="font-bold text-white mb-2">Role Guides</h3>
              <p className="text-sm text-gray-400">Sign in to open the guide for your role</p>
            </Link>
          </div>
        </div>
      </PublicBandSection>

      <PublicBandSection band={3} className="px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white/5 border border-white/10 rounded-xl p-6 sticky top-4">
                <h3 className="text-lg font-bold mb-4 text-white">Categories</h3>
                <div className="space-y-2">
                  {categories.map(category => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition ${
                        selectedCategory === category.id
                          ? 'bg-gradient-to-r from-violet-600 to-blue-500 text-white'
                          : 'hover:bg-white/5 text-gray-400 hover:text-white'
                      }`}
                    >
                      <span className="mr-2">{category.icon}</span>
                      {category.name}
                      <span className={`float-right text-sm ${
                        selectedCategory === category.id ? 'text-white' : 'text-gray-500'
                      }`}>
                        {category.count}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Articles */}
            <div className="lg:col-span-3">
              <h2 className="text-2xl font-bold mb-6 text-white">
                {categories.find(c => c.id === selectedCategory)?.name}
              </h2>
              <div className="space-y-4">
                {filteredArticles.map(article => (
                  <div key={article.id} className="bg-white/5 border border-white/10 rounded-xl p-6 hover:bg-white/10 transition">
                    <h3 className="text-lg font-bold mb-2 text-white hover:text-violet-400 cursor-pointer">
                      {article.title}
                    </h3>
                    <p className="text-gray-400 mb-3">{article.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <span>{article.views} views</span>
                      <span className="text-violet-400 hover:underline cursor-pointer">Read more →</span>
                    </div>
                  </div>
                ))}
              </div>

              {filteredArticles.length === 0 && (
                <div className="text-center py-12 bg-white/5 border border-white/10 rounded-xl">
                  <p className="text-gray-500">No articles in this category yet.</p>
                </div>
              )}

              {/* Still Need Help */}
              <div className="mt-12 bg-gradient-to-r from-violet-600 to-blue-600 rounded-xl p-8 text-white text-center">
                <h3 className="text-2xl font-bold mb-4">Still need help?</h3>
                <p className="text-lg opacity-90 mb-6">
                  Our support team is here to assist you with any questions.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Link
                    to="/support"
                    className="px-6 py-3 bg-white text-violet-600 rounded-lg font-semibold hover:bg-gray-100 transition"
                  >
                    Contact Support
                  </Link>
                  <Link
                    to="/faq"
                    className="px-6 py-3 bg-white/20 text-white rounded-lg font-semibold hover:bg-white/30 transition"
                  >
                    View FAQ
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PublicBandSection>

      <Footer />
    </PublicMarketingLayout>
  );
};

export default Help;
